import React, { useEffect, useMemo, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import NotesIcon from '@mui/icons-material/Notes'
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew'
import { get, limitToLast, query, ref } from 'firebase/database'
import { Link, useParams } from 'react-router'
import cnergiLogo from '../resources/CNERGI_PNG@4x.png'
import {
  getLoginSession,
  getProfileDeviceIds,
  getUserProfile,
} from '../services/authApi'
import { db } from '../services/firebase'
import './MobileDashboardPage.css'

const LOG_FETCH_LIMIT = 1440
const MOBILE_PIE_SIZE = 180
const MOBILE_PIE_CENTER = MOBILE_PIE_SIZE / 2
const MOBILE_PIE_RADIUS = 68

function toNumber(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string') {
    const parsed = Number(value.trim())

    return Number.isFinite(parsed) ? parsed : null
  }

  return null
}

function getLogPayload(log = {}) {
  if (!log || typeof log !== 'object') {
    return {}
  }

  return log?.data && typeof log.data === 'object' ? log.data : log
}

function getPowerImport(log) {
  const payload = getLogPayload(log)

  return toNumber(payload.powerImport ?? payload.gridImport ?? payload.import ?? payload.importPower ?? log?.devicedata?.meter?.import?.W)
}

function getPowerExport(log) {
  const payload = getLogPayload(log)

  return toNumber(payload.powerExport ?? payload.gridExport ?? payload.export ?? payload.exportPower ?? log?.devicedata?.meter?.export?.W)
}

function getEstimatedSavings(log) {
  const payload = getLogPayload(log)

  return toNumber(
    payload.estimatedSavings
      ?? payload.estimatedSavingsPhp
      ?? payload.savings
      ?? payload.savingsPhp
      ?? log?.estimatedSavings
      ?? log?.estimatedSavingsPhp
      ?? log?.savings
      ?? log?.savingsPhp
  )
}

function getStatusCode(log) {
  const payload = getLogPayload(log)
  const statusCode = payload?.ml?.status ?? payload?.status ?? payload?.statusCode ?? log?.ml?.status

  return statusCode === undefined || statusCode === null || statusCode === '' ? 'N/A' : String(statusCode)
}

function getFaultStatus(log) {
  const payload = getLogPayload(log)
  const faultStatus = payload?.ml?.fault ?? payload?.fault ?? payload?.faultStatus ?? log?.ml?.fault

  return faultStatus === undefined || faultStatus === null || faultStatus === '' ? 'N/A' : String(faultStatus)
}

function getRelayEnabled(log, relayKey) {
  const payload = getLogPayload(log)
  const control = payload?.config?.control || payload?.control || log?.config?.control || log?.control || {}
  const relayName = relayKey === 'rly1' ? 'relay1' : 'relay2'
  const value = control[relayKey] ?? control[relayName] ?? payload?.[relayKey] ?? payload?.[relayName]

  return value === true || String(value) === '1'
}

function formatKw(value) {
  return value === null ? 'N/A' : `${value.toFixed(2)} kW`
}

function formatPhp(value) {
  return value === null ? 'N/A' : `PHP ${value.toFixed(2)}`
}

function getAnalyticsPoints(logs) {
  return logs
    .map((log) => ({
      importValue: getPowerImport(log),
      exportValue: getPowerExport(log),
    }))
    .filter((point) => point.importValue !== null || point.exportValue !== null)
}

function getTotal(values) {
  return values
    .filter((value) => value !== null)
    .reduce((sum, value) => sum + value, 0)
}

function getAverage(values) {
  const validValues = values.filter((value) => value !== null)

  return validValues.length ? validValues.reduce((sum, value) => sum + value, 0) / validValues.length : null
}

function getPeak(values) {
  const validValues = values.filter((value) => value !== null)

  return validValues.length ? Math.max(...validValues) : null
}

function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180

  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  }
}

function getPieSlicePath(startAngle, endAngle) {
  const start = polarToCartesian(MOBILE_PIE_CENTER, MOBILE_PIE_CENTER, MOBILE_PIE_RADIUS, endAngle)
  const end = polarToCartesian(MOBILE_PIE_CENTER, MOBILE_PIE_CENTER, MOBILE_PIE_RADIUS, startAngle)
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1'

  return [
    `M ${MOBILE_PIE_CENTER} ${MOBILE_PIE_CENTER}`,
    `L ${start.x.toFixed(2)} ${start.y.toFixed(2)}`,
    `A ${MOBILE_PIE_RADIUS} ${MOBILE_PIE_RADIUS} 0 ${largeArcFlag} 0 ${end.x.toFixed(2)} ${end.y.toFixed(2)}`,
    'Z',
  ].join(' ')
}

function normalizeFirebaseLogs(rawLogs) {
  const logs = Array.isArray(rawLogs)
    ? rawLogs
        .map((value, index) => {
          if (!value || typeof value !== 'object') {
            return null
          }

          return {
            _key: String(index),
            timestamp: value.timestamp ?? value.ts ?? value.time ?? index,
            ...value,
          }
        })
        .filter(Boolean)
    : Object.entries(rawLogs || {}).map(([key, value]) => ({
        _key: key,
        timestamp: value?.timestamp ?? value?.ts ?? value?.time ?? key,
        ...(value && typeof value === 'object' ? value : {}),
      }))

  return logs
    .filter((log) => Number.isFinite(Number(log?.timestamp)))
    .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))
}

function getPerformanceLabel(log) {
  const statusCode = getStatusCode(log)

  if (statusCode === '1') {
    return 'Grid'
  }

  if (statusCode === '2') {
    return 'Warning'
  }

  if (statusCode === '3') {
    return 'Normal'
  }

  return statusCode === 'N/A' ? 'Unavailable' : `Status ${statusCode}`
}

function MobileEnergyPieChart({ importTotal, exportTotal }) {
  const total = importTotal + exportTotal

  if (total <= 0) {
    return (
      <div className="mobile-dashboard__chart-empty">No analytics data</div>
    )
  }

  const importPercent = (importTotal / total) * 100
  const importEndAngle = importPercent === 100 ? 359.99 : (importPercent / 100) * 360
  const slices = []

  if (importTotal > 0) {
    slices.push({
      key: 'import',
      path: getPieSlicePath(0, importEndAngle),
    })
  }

  if (exportTotal > 0) {
    slices.push({
      key: 'export',
      path: getPieSlicePath(importEndAngle, 359.99),
    })
  }

  return (
    <div className="mobile-dashboard__pie-wrap">
      <svg className="mobile-dashboard__pie" viewBox={`0 0 ${MOBILE_PIE_SIZE} ${MOBILE_PIE_SIZE}`} role="img" aria-label="Import and export energy mix">
        <circle className="mobile-dashboard__pie-track" cx={MOBILE_PIE_CENTER} cy={MOBILE_PIE_CENTER} r={MOBILE_PIE_RADIUS} />
        {slices.map((slice) => (
          <path className={`mobile-dashboard__pie-slice mobile-dashboard__pie-slice--${slice.key}`} d={slice.path} key={slice.key} />
        ))}
        <circle className="mobile-dashboard__pie-hole" cx={MOBILE_PIE_CENTER} cy={MOBILE_PIE_CENTER} r="38" />
        <text className="mobile-dashboard__pie-label" x={MOBILE_PIE_CENTER} y={MOBILE_PIE_CENTER - 4}>Total</text>
        <text className="mobile-dashboard__pie-total" x={MOBILE_PIE_CENTER} y={MOBILE_PIE_CENTER + 15}>{formatKw(total)}</text>
      </svg>

      <div className="mobile-dashboard__chart-pills">
        <span><i className="mobile-dashboard__mark mobile-dashboard__mark--import"></i>Import {importPercent.toFixed(0)}%</span>
        <span><i className="mobile-dashboard__mark mobile-dashboard__mark--export"></i>Export {(100 - importPercent).toFixed(0)}%</span>
      </div>
    </div>
  )
}

function MobileBarChart({ averageImport, averageExport, peakImport, peakExport }) {
  const bars = [
    { label: 'Avg I', value: averageImport, type: 'import' },
    { label: 'Avg E', value: averageExport, type: 'export' },
    { label: 'Peak I', value: peakImport, type: 'import' },
    { label: 'Peak E', value: peakExport, type: 'export' },
  ]
  const maxValue = Math.max(...bars.map((bar) => bar.value || 0), 1)

  return (
    <div className="mobile-dashboard__bars" role="img" aria-label="Average and peak load comparison">
      {bars.map((bar) => (
        <div className="mobile-dashboard__bar" key={bar.label}>
          <span>{bar.label}</span>
          <div>
            <i
              className={`mobile-dashboard__bar-fill mobile-dashboard__bar-fill--${bar.type}`}
              style={{ width: `${Math.max(((bar.value || 0) / maxValue) * 100, bar.value ? 8 : 0)}%` }}
            ></i>
          </div>
          <b>{formatKw(bar.value)}</b>
        </div>
      ))}
    </div>
  )
}

function MobileSavingsChart({ savingsTotal }) {
  return (
    <div className="mobile-dashboard__savings-chart" role="img" aria-label="Estimated savings placeholder">
      <div className="mobile-dashboard__savings-ring">
        <span>{savingsTotal === null ? 'N/A' : formatPhp(savingsTotal)}</span>
      </div>
      <div className="mobile-dashboard__mini-bars" aria-hidden="true">
        <i></i>
        <i></i>
        <i></i>
        <i></i>
      </div>
    </div>
  )
}

function MobileDashboardPage({ isDarkMode = false, onToggleTheme }) {
  const { deviceId: routeDeviceId = '' } = useParams()
  const [deviceId, setDeviceId] = useState(routeDeviceId)
  const [recentLogs, setRecentLogs] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const latestLog = recentLogs[0] || null
  const logsPath = deviceId ? `/mobile/dashboard/${encodeURIComponent(deviceId)}/logs` : '/mobile/dashboard/logs'
  const metrics = useMemo(() => ([
    { label: 'Grid Import', value: formatKw(getPowerImport(latestLog)) },
    { label: 'Grid Export', value: formatKw(getPowerExport(latestLog)) },
    { label: 'Savings', value: formatPhp(getEstimatedSavings(latestLog)) },
    { label: 'Status', value: getPerformanceLabel(latestLog) },
  ]), [latestLog])
  const analytics = useMemo(() => {
    const points = getAnalyticsPoints(recentLogs)
    const importValues = points.map((point) => point.importValue)
    const exportValues = points.map((point) => point.exportValue)
    const savingsValues = recentLogs.map((log) => getEstimatedSavings(log))

    return {
      averageImport: getAverage(importValues),
      averageExport: getAverage(exportValues),
      peakImport: getPeak(importValues),
      peakExport: getPeak(exportValues),
      importTotal: getTotal(importValues),
      exportTotal: getTotal(exportValues),
      savingsTotal: savingsValues.some((value) => value !== null) ? getTotal(savingsValues) : null,
      sampleCount: points.length,
    }
  }, [recentLogs])

  useEffect(() => {
    let isMounted = true
    const loginSession = getLoginSession()

    if (routeDeviceId) {
      setDeviceId(routeDeviceId)
      return () => {
        isMounted = false
      }
    }

    if (!loginSession?.user?.email) {
      setIsLoading(false)
      return () => {
        isMounted = false
      }
    }

    getUserProfile(loginSession.user.email)
      .then((profileResponse) => {
        const [primaryDeviceId = ''] = getProfileDeviceIds(profileResponse)

        if (isMounted) {
          setDeviceId(primaryDeviceId)
        }
      })
      .catch((error) => {
        console.error('Mobile dashboard profile request failed:', error)

        if (isMounted) {
          setDeviceId('')
        }
      })

    return () => {
      isMounted = false
    }
  }, [routeDeviceId])

  useEffect(() => {
    let isMounted = true

    if (!deviceId) {
      setRecentLogs([])
      setIsLoading(false)
      return () => {
        isMounted = false
      }
    }

    setIsLoading(true)

    const logsRef = query(ref(db, `EMS/Logs/${deviceId}/data`), limitToLast(LOG_FETCH_LIMIT))

    get(logsRef)
      .then((snapshot) => {
        if (isMounted) {
          setRecentLogs(snapshot.exists() ? normalizeFirebaseLogs(snapshot.val()) : [])
        }
      })
      .catch((error) => {
        console.error(`Unable to fetch Firebase logs for ${deviceId}:`, error)

        if (isMounted) {
          setRecentLogs([])
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [deviceId])

  return (
    <main className="mobile-dashboard">
      <header className="mobile-dashboard__topbar">
        <Link className="mobile-dashboard__icon-link" to="/mobile/maindash" aria-label="Back to mobile main dashboard">
          <ArrowBackIcon aria-hidden="true" />
        </Link>

        <Link className="mobile-dashboard__brand" to="/" aria-label="CNERGI home">
          <img src={cnergiLogo} alt="CNERGI" />
        </Link>

        <button
          className="mobile-dashboard__icon-button"
          type="button"
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          onClick={onToggleTheme}
        >
          {isDarkMode ? <LightModeIcon aria-hidden="true" /> : <DarkModeIcon aria-hidden="true" />}
        </button>
      </header>

      <section className="mobile-dashboard__summary" aria-labelledby="mobile-dashboard-title">
        <p>Device Dashboard</p>
        <h1 id="mobile-dashboard-title">{deviceId || 'No device selected'}</h1>
        <span>{isLoading ? 'Loading latest readings...' : `Status code: ${getStatusCode(latestLog)} | Fault: ${getFaultStatus(latestLog)}`}</span>
      </section>

      <section className="mobile-dashboard__metrics" aria-label="Device metrics">
        {metrics.map((metric) => (
          <article className="mobile-dashboard__metric" key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
          </article>
        ))}
      </section>

      <section className="mobile-dashboard__analytics" aria-labelledby="mobile-analytics-title">
        <div className="mobile-dashboard__section-header">
          <div>
            <h2 id="mobile-analytics-title">Analytics</h2>
            <p>{analytics.sampleCount || 0} recent readings</p>
          </div>
        </div>

        <article className="mobile-dashboard__chart-card mobile-dashboard__chart-card--mix">
          <h3>Energy Mix</h3>
          <MobileEnergyPieChart importTotal={analytics.importTotal} exportTotal={analytics.exportTotal} />
        </article>

        <article className="mobile-dashboard__chart-card">
          <h3>Load Comparison</h3>
          <MobileBarChart
            averageImport={analytics.averageImport}
            averageExport={analytics.averageExport}
            peakImport={analytics.peakImport}
            peakExport={analytics.peakExport}
          />
        </article>

        <article className="mobile-dashboard__chart-card">
          <h3>Savings</h3>
          <MobileSavingsChart savingsTotal={analytics.savingsTotal} />
        </article>
      </section>

      <section className="mobile-dashboard__controls" aria-labelledby="mobile-controls-title">
        <div className="mobile-dashboard__section-header">
          <h2 id="mobile-controls-title">Controls</h2>
          <PowerSettingsNewIcon aria-hidden="true" />
        </div>

        <div className="mobile-dashboard__relay-row">
          <span>Relay 1</span>
          <mark>{getRelayEnabled(latestLog, 'rly1') ? 'On' : 'Off'}</mark>
        </div>
        <div className="mobile-dashboard__relay-row">
          <span>Relay 2</span>
          <mark>{getRelayEnabled(latestLog, 'rly2') ? 'On' : 'Off'}</mark>
        </div>
      </section>

      <section className="mobile-dashboard__logs-entry" aria-labelledby="mobile-logs-title">
        <div>
          <h2 id="mobile-logs-title">Logs</h2>
          <p>{recentLogs.length ? `${recentLogs.length} readings available` : 'Open device logs'}</p>
        </div>
        <Link className="mobile-dashboard__logs-button" to={logsPath}>
          <NotesIcon aria-hidden="true" />
          View Logs
        </Link>
      </section>
    </main>
  )
}

export default MobileDashboardPage
